import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { facturesAPI, walletAPI } from '../services/api';

const card = { background:'#fff', border:'0.5px solid rgba(0,0,0,0.07)', borderRadius:'12px', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' };

const PLANS = [
  { key:'starter',    label:'Starter',    price:0,   desc:"Jusqu'à 50 dossiers/mois",          recommended:false,
    features:['Dossiers + suivi INPI','Wallet INPI intégré','Facturation simple','Agent Suivi INPI inclus'] },
  { key:'pro',        label:'Pro',        price:200, desc:'50 à 200 dossiers/mois',             recommended:false,
    features:['Tout Starter +','Dashboard pilotage','Scorecard qualité','Agent Conformité inclus','Rapports automatiques'] },
  { key:'enterprise', label:'Enterprise', price:500, desc:'LegalTechs & volumes importants',    recommended:true,
    features:['Tout Pro +','Forecasting & prévisions','Benchmarking sectoriel','API & webhooks avancés','Connecteurs Zendesk/Intercom','SLA garanti'] },
];

const ETP_OPTIONS = [
  { val:1, label:'1 formaliste',  price:1500, desc:'≤ 100 dos/mois' },
  { val:2, label:'2 formalistes', price:2800, desc:'≤ 200 dos/mois' },
  { val:3, label:'3 formalistes', price:3900, desc:'≤ 350 dos/mois' },
  { val:5, label:'5 formalistes', price:5800, desc:'≤ 600 dos/mois' },
  { val:8, label:'8 formalistes', price:8400, desc:'Volumes illimités' },
];

const STATUS_MAP = {
  draft:     { label:'Brouillon', color:'rgba(26,25,22,0.45)', bg:'rgba(26,25,22,0.06)' },
  envoyee:   { label:'En cours',  color:'#d97706',             bg:'rgba(217,119,6,0.08)' },
  payee:     { label:'Payée',     color:'#1A7A4A',             bg:'rgba(26,122,74,0.08)' },
  en_retard: { label:'En retard', color:'#B03020',             bg:'rgba(176,48,32,0.08)' },
};

const TX_LABELS = {
  credit:         { label:'Crédit',        color:'#1A7A4A', bg:'rgba(26,122,74,0.08)' },
  debit_inpi:     { label:'Avance INPI',   color:'#B03020', bg:'rgba(176,48,32,0.08)' },
  debit_greffe:   { label:'Avance greffe', color:'#B03020', bg:'rgba(176,48,32,0.08)' },
  debit_jal:      { label:'Frais JAL',     color:'#B03020', bg:'rgba(176,48,32,0.08)' },
  remboursement:  { label:'Remboursement', color:'#1A7A4A', bg:'rgba(26,122,74,0.08)' },
};

export default function Facturation() {
  const { user } = useAuth();
  const plan = user?.workspace?.plan || 'starter';
  const [tab, setTab]       = useState('plan');
  const [selEtp, setSelEtp] = useState(1);
  const [selPlan, setSelPlan] = useState(plan);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [factures, setFactures] = useState([]);
  const [facturesLoading, setFacturesLoading] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    if (tab === 'factures' && factures.length === 0) {
      setFacturesLoading(true);
      facturesAPI.list()
        .then(r => setFactures(r.data || []))
        .catch(() => {})
        .finally(() => setFacturesLoading(false));
    }
    if (tab === 'wallet' && !walletData) {
      setWalletLoading(true);
      Promise.all([walletAPI.get(), walletAPI.transactions({ limit: 20 })])
        .then(([wRes, tRes]) => {
          setWalletData(wRes.data);
          setTransactions(tRes.data || []);
        })
        .catch(() => {})
        .finally(() => setWalletLoading(false));
    }
  }, [tab]);

  const planPrice = PLANS.find(p => p.key === selPlan)?.price || 0;
  const etpPrice  = ETP_OPTIONS.find(e => e.val === selEtp)?.price || 0;
  const total     = planPrice + etpPrice;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:'1.5rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'18px', fontWeight:600, color:'#1A1916' }}>Abonnement & Facturation</div>
        <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.4)', marginTop:'2px' }}>Gérez votre plan, vos ETP et vos add-ons</div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid rgba(0,0,0,0.07)', marginBottom:'1.5rem' }}>
        {[{k:'plan',l:'Plan & ETP'},{k:'factures',l:'Factures'},{k:'wallet',l:'Wallet INPI'}].map(t => (
          <div key={t.k} onClick={() => setTab(t.k)} style={{
            padding:'8px 18px', fontSize:'13px', cursor:'pointer',
            borderBottom: tab === t.k ? '2px solid #1A1916' : '2px solid transparent',
            color:   tab === t.k ? '#1A1916' : 'rgba(26,25,22,0.4)',
            fontWeight: tab === t.k ? 500 : 300,
          }}>{t.l}</div>
        ))}
      </div>

      {/* ── Plan & ETP ── */}
      {tab === 'plan' && (
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:'#1A1916', marginBottom:'14px' }}>Choisissez votre plan plateforme</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'1.5rem' }}>
            {PLANS.map(p => {
              const isCurrent = p.key === plan;
              const isSel     = p.key === selPlan;
              return (
                <div key={p.key} onClick={() => setSelPlan(p.key)} style={{
                  ...card, padding:'1.25rem', cursor:'pointer', position:'relative',
                  border: p.recommended ? `1.5px solid #1A55A0` : isSel ? '1.5px solid #B8860B' : '0.5px solid rgba(0,0,0,0.07)',
                  transition:'all 0.15s',
                }}>
                  {isCurrent && <div style={{ position:'absolute', top:'-11px', left:'50%', transform:'translateX(-50%)', background:'#B8860B', color:'#fff', fontSize:'10px', fontWeight:500, padding:'2px 10px', borderRadius:'20px', whiteSpace:'nowrap' }}>Plan actuel</div>}
                  {p.recommended && !isCurrent && <div style={{ position:'absolute', top:'-11px', left:'50%', transform:'translateX(-50%)', background:'#1A55A0', color:'#fff', fontSize:'10px', fontWeight:500, padding:'2px 10px', borderRadius:'20px', whiteSpace:'nowrap' }}>Recommandé</div>}
                  <div style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:600, color:'#1A1916', marginBottom:'6px' }}>{p.label}</div>
                  <div style={{ fontFamily:'Syne, sans-serif', fontSize:'26px', fontWeight:700, color:'#1A1916', lineHeight:1 }}>
                    {p.price === 0 ? '0' : p.price} <span style={{ fontSize:'14px', fontWeight:300, color:'rgba(26,25,22,0.4)' }}>€ /mois</span>
                  </div>
                  <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.4)', margin:'6px 0 12px' }}>{p.desc}</div>
                  {p.features.map(f => (
                    <div key={f} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'rgba(26,25,22,0.6)', padding:'2px 0' }}>
                      <span style={{ color:'#1A7A4A', fontWeight:500 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* ETP */}
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:'#1A1916', marginBottom:'12px' }}>Formalistes dédiés (ETP)</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px', marginBottom:'1.5rem' }}>
            {ETP_OPTIONS.map(e => (
              <div key={e.val} onClick={() => setSelEtp(e.val)} style={{
                ...card, padding:'14px', textAlign:'center', cursor:'pointer',
                border: selEtp === e.val ? '1.5px solid #B8860B' : '0.5px solid rgba(0,0,0,0.07)',
                background: selEtp === e.val ? 'rgba(184,134,11,0.03)' : '#fff',
              }}>
                <div style={{ fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:'#1A1916', marginBottom:'2px' }}>{e.label}</div>
                <div style={{ fontFamily:'Syne, sans-serif', fontSize:'18px', fontWeight:700, color:'#B8860B', marginBottom:'2px' }}>{e.price.toLocaleString('fr-FR')} €</div>
                <div style={{ fontSize:'10px', color:'rgba(26,25,22,0.35)' }}>{e.desc}</div>
              </div>
            ))}
          </div>

          {/* Récap */}
          <div style={{ ...card, padding:'1.25rem' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:'#1A1916', marginBottom:'1rem' }}>Récapitulatif mensuel estimé</div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'0.5px solid rgba(0,0,0,0.07)' }}>
                  {['Description','Qté','Montant'].map(h => (
                    <th key={h} style={{ textAlign: h==='Montant' ? 'right' : 'left', padding:'8px 0', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.28)', fontWeight:400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom:'0.5px solid rgba(0,0,0,0.05)' }}>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'#1A1916' }}>Plan {PLANS.find(p=>p.key===selPlan)?.label}</td>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'rgba(26,25,22,0.45)' }}>1</td>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'#1A1916', textAlign:'right' }}>{planPrice.toLocaleString('fr-FR')} €</td>
                </tr>
                <tr>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'#1A1916' }}>{ETP_OPTIONS.find(e=>e.val===selEtp)?.label} ETP</td>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'rgba(26,25,22,0.45)' }}>1</td>
                  <td style={{ padding:'10px 0', fontSize:'12px', color:'#1A1916', textAlign:'right' }}>{etpPrice.toLocaleString('fr-FR')} €</td>
                </tr>
              </tbody>
            </table>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'12px', marginTop:'8px', borderTop:'1px solid rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize:'12px', color:'rgba(26,25,22,0.45)' }}>Total HT / mois</div>
              <div style={{ fontFamily:'Syne, sans-serif', fontSize:'20px', fontWeight:700, color:'#1A1916' }}>{total.toLocaleString('fr-FR')} €</div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'1rem' }}>
              <button style={{ background:'transparent', border:'0.5px solid rgba(0,0,0,0.12)', borderRadius:'7px', padding:'7px 16px', fontSize:'12px', cursor:'pointer', color:'rgba(26,25,22,0.6)', fontFamily:'DM Sans, sans-serif' }}>
                Télécharger devis
              </button>
              <button onClick={() => { if (selPlan !== plan) setShowUpgradeModal(true); }} style={{ background:'#B8860B', color:'#fff', border:'none', padding:'7px 18px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif', opacity: selPlan === plan ? 0.5 : 1 }}>
                {selPlan === plan ? 'Plan actuel' : 'Changer de plan →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Factures ── */}
      {tab === 'factures' && (
        <div>
          {facturesLoading ? (
            <div style={{ ...card, padding:'2rem', textAlign:'center', color:'rgba(26,25,22,0.4)', fontSize:'12px' }}>Chargement des factures…</div>
          ) : factures.length === 0 ? (
            <div style={{ ...card, padding:'2rem', textAlign:'center' }}>
              <div style={{ fontSize:'13px', color:'rgba(26,25,22,0.4)', marginBottom:'4px' }}>Aucune facture</div>
              <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.25)' }}>Vos factures apparaîtront ici à la fin de chaque période.</div>
            </div>
          ) : (
            <div style={card}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'0.5px solid rgba(0,0,0,0.07)' }}>
                    {['Référence','Période','Statut','Montant'].map(h => (
                      <th key={h} style={{ textAlign: h==='Montant' ? 'right':'left', padding:'10px 16px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.28)', fontWeight:400 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {factures.map((f,i) => {
                    const st = STATUS_MAP[f.status] || STATUS_MAP.draft;
                    const periode = f.periode_mois ? new Date(f.periode_mois).toLocaleDateString('fr-FR', { month:'long', year:'numeric' }) : '—';
                    return (
                      <tr key={f.id} style={{ borderBottom: i<factures.length-1 ? '0.5px solid rgba(0,0,0,0.05)':'none' }}
                        onMouseEnter={e => e.currentTarget.style.background='#F5F3EE'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}
                      >
                        <td style={{ padding:'11px 16px', fontSize:'12px', fontWeight:500, color:'#1A1916', fontFamily:'monospace' }}>{f.reference}</td>
                        <td style={{ padding:'11px 16px', fontSize:'12px', color:'rgba(26,25,22,0.6)', textTransform:'capitalize' }}>{periode}</td>
                        <td style={{ padding:'11px 16px' }}><span style={{ fontSize:'11px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:st.color, background:st.bg }}>{st.label}</span></td>
                        <td style={{ padding:'11px 16px', fontSize:'12px', fontWeight:500, color:'#1A1916', textAlign:'right' }}>{Number(f.montant_total||0).toLocaleString('fr-FR')} €</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Wallet INPI ── */}
      {tab === 'wallet' && (
        <div>
          {walletLoading ? (
            <div style={{ ...card, padding:'2rem', textAlign:'center', color:'rgba(26,25,22,0.4)', fontSize:'12px' }}>Chargement…</div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'1rem' }}>
                {[
                  { l:'Solde actuel', v:`${Number(walletData?.balance||0).toLocaleString('fr-FR')} €`, c: Number(walletData?.balance||0) < Number(walletData?.alert_threshold||3000) ? '#B03020' : '#1A7A4A' },
                  { l:'Seuil alerte', v:`${Number(walletData?.alert_threshold||0).toLocaleString('fr-FR')} €`, c:'#B03020' },
                  { l:'Transactions', v:walletData?.transactions_count || 0, c:'#1A1916' },
                ].map(k => (
                  <div key={k.l} style={{ ...card, padding:'14px 16px' }}>
                    <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.28)', marginBottom:'4px' }}>{k.l}</div>
                    <div style={{ fontFamily:'Syne, sans-serif', fontSize:'24px', fontWeight:700, color:k.c }}>{k.v}</div>
                  </div>
                ))}
              </div>
              {transactions.length === 0 ? (
                <div style={{ ...card, padding:'2rem', textAlign:'center', color:'rgba(26,25,22,0.4)', fontSize:'12px' }}>Aucune transaction</div>
              ) : (
                <div style={{ ...card }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ borderBottom:'0.5px solid rgba(0,0,0,0.07)' }}>
                      {['Date','Type','Référence','Montant','Solde après'].map(h => (
                        <th key={h} style={{ textAlign: h==='Montant'||h==='Solde après' ? 'right':'left', padding:'10px 16px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:'rgba(26,25,22,0.28)', fontWeight:400 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {transactions.map((tx,i) => {
                        const txStyle = TX_LABELS[tx.type] || TX_LABELS.credit;
                        const isCredit = tx.type === 'credit' || tx.type === 'remboursement';
                        return (
                          <tr key={tx.id} style={{ borderBottom: i<transactions.length-1 ? '0.5px solid rgba(0,0,0,0.05)':'none' }}>
                            <td style={{ padding:'11px 16px', fontSize:'12px', color:'rgba(26,25,22,0.45)' }}>{new Date(tx.created_at).toLocaleDateString('fr-FR')}</td>
                            <td style={{ padding:'11px 16px' }}><span style={{ fontSize:'11px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:txStyle.color, background:txStyle.bg }}>{txStyle.label}</span></td>
                            <td style={{ padding:'11px 16px', fontSize:'12px', color:'rgba(26,25,22,0.55)', fontFamily:'monospace' }}>{tx.reference || '—'}</td>
                            <td style={{ padding:'11px 16px', fontSize:'12px', fontWeight:500, color:isCredit ? '#1A7A4A' : '#B03020', textAlign:'right' }}>{isCredit ? '+' : '−'}{Math.abs(tx.montant).toLocaleString('fr-FR')} €</td>
                            <td style={{ padding:'11px 16px', fontSize:'12px', color:'rgba(26,25,22,0.55)', textAlign:'right' }}>{Number(tx.balance_after||0).toLocaleString('fr-FR')} €</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* Modal upgrade */}
      {showUpgradeModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={() => setShowUpgradeModal(false)}>
          <div style={{ background:'#fff', borderRadius:'14px', padding:'2rem', maxWidth:'420px', width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.12)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:'#1A1916', marginBottom:'8px' }}>
              Changer pour le plan {PLANS.find(p => p.key === selPlan)?.label}
            </div>
            <div style={{ fontSize:'13px', color:'rgba(26,25,22,0.55)', lineHeight:1.6, marginBottom:'1.25rem' }}>
              Pour modifier votre plan, contactez notre équipe. Nous traiterons votre demande sous 24h.
            </div>
            <div style={{ background:'#F5F3EE', borderRadius:'8px', padding:'14px 16px', marginBottom:'1.25rem' }}>
              <div style={{ fontSize:'11px', color:'rgba(26,25,22,0.4)', marginBottom:'6px' }}>Contactez-nous</div>
              <a href="mailto:contact@swap-business.com?subject=Upgrade%20Lexback%20-%20Plan%20{selPlan}" style={{ fontSize:'14px', color:'#B8860B', fontWeight:500, textDecoration:'none' }}>
                contact@swap-business.com
              </a>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
              <button onClick={() => setShowUpgradeModal(false)} style={{ background:'transparent', border:'1px solid rgba(0,0,0,0.1)', padding:'7px 16px', borderRadius:'7px', fontSize:'12px', cursor:'pointer', color:'rgba(26,25,22,0.6)', fontFamily:'DM Sans, sans-serif' }}>Fermer</button>
              <a href={`mailto:contact@swap-business.com?subject=Demande upgrade Lexback - Plan ${selPlan}`} style={{ background:'#B8860B', color:'#fff', border:'none', padding:'7px 18px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>Envoyer un email</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
